import { EventEmitter, Injectable } from '@angular/core';

const SHOW_FRIDGE_ADDRESS_STORAGE_KEY = 'serviceman_show_fridge_address';
// the two keys below predate this service; kept so existing devices keep their choice
const RACK_SIZE_STORAGE_KEY = 'fridgePageRackSize';
const SHOW_AI_RECOGNITION_STORAGE_KEY = 'showAiRecognition';

export type RackSize = 'big' | 'small';

/** Per-device UI preferences of the service man app, kept in localStorage like the chosen language. */
@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  public readonly change = new EventEmitter<void>();

  private _showFridgeAddress = SettingsService.readBoolean(SHOW_FRIDGE_ADDRESS_STORAGE_KEY, true);
  private _rackSize: RackSize = SettingsService.readString(RACK_SIZE_STORAGE_KEY) === 'small' ? 'small' : 'big';
  private _showAiRecognition = SettingsService.readBoolean(SHOW_AI_RECOGNITION_STORAGE_KEY, false);

  /** Address line in the fridge list. On by default. */
  get showFridgeAddress(): boolean {
    return this._showFridgeAddress;
  }

  set showFridgeAddress(value: boolean) {
    this._showFridgeAddress = value;
    SettingsService.write(SHOW_FRIDGE_ADDRESS_STORAGE_KEY, String(value));
    this.change.emit();
  }

  /** Size of the racks on the fridge page. Big by default. */
  get rackSize(): RackSize {
    return this._rackSize;
  }

  set rackSize(value: RackSize) {
    this._rackSize = value;
    SettingsService.write(RACK_SIZE_STORAGE_KEY, value);
    this.change.emit();
  }

  get isRackSmall(): boolean {
    return this._rackSize === 'small';
  }

  /** AI recognition quality on product cards; only shown in the big rack view. Off by default. */
  get showAiRecognition(): boolean {
    return this._showAiRecognition;
  }

  set showAiRecognition(value: boolean) {
    this._showAiRecognition = value;
    SettingsService.write(SHOW_AI_RECOGNITION_STORAGE_KEY, String(value));
    this.change.emit();
  }

  private static readString(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private static readBoolean(key: string, fallback: boolean): boolean {
    const value = SettingsService.readString(key);
    return value === null ? fallback : value === 'true';
  }

  private static write(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // storage unavailable (private mode) — the setting just isn't remembered
    }
  }
}
